import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import Snackbar from "@mui/material/Snackbar";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import { useTheme, alpha } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import PrintIcon from "@mui/icons-material/Print";
import CodeIcon from "@mui/icons-material/Code";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import {
  getSessionReport,
  StructuredReport,
  ReportEnvironmentGroup,
  ReportProcedureStep,
} from "./utils/backend-utils";

// Document layout — A4-ish width, serif-leaning sans, generous margins.
// The whole page is white with a thin shadow, so it visually reads as a
// document on a desk rather than a chat continuation.
const DOC_MAX_WIDTH = 880;
const TOC_WIDTH = 260;

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Render an ISO timestamp as "28 Apr 2026, 15:32 UTC". Empty / unparseable
// → empty string so the calling JSX can fall back to a placeholder dash.
function formatDate(iso: string | undefined): string {
  if (!iso) return "";
  const s = iso.replace("Z", "+00:00");
  const dt = new Date(s);
  if (isNaN(dt.getTime())) return iso;
  const d = dt.getUTCDate();
  const m = MONTHS[dt.getUTCMonth()];
  const y = dt.getUTCFullYear();
  const hh = String(dt.getUTCHours()).padStart(2, "0");
  const mm = String(dt.getUTCMinutes()).padStart(2, "0");
  return `${d} ${m} ${y}, ${hh}:${mm} UTC`;
}

interface Section {
  id: string;
  number: number;
  title: string;
  visible: boolean;
}

function buildSections(report: StructuredReport): Section[] {
  if (report.report_type === 'interrogation') {
    return [
      { id: "executive-summary", number: 1, title: "Summary", visible: !!report.executive_summary },
      { id: "environment", number: 2, title: "Entities", visible: report.environment.length > 0 },
      { id: "post-install", number: 3, title: report.post_label || "Open Threads", visible: report.post_install_tasks.length > 0 },
      { id: "references", number: 4, title: "Claim References", visible: report.references.length > 0 },
      { id: "knowledge-graph", number: 5, title: "Knowledge Graph", visible: !!report.interrogation_turtle?.trim() },
      { id: "metrics", number: 6, title: "Session Metrics", visible: !!report.session_metrics },
      { id: "sign-off", number: 7, title: "Sign-Off", visible: true },
    ];
  }

  // Section 5 / section 7 labels come from the report payload so each
  // template (install, tuning, ...) can supply its own wording. Falls back
  // to neutral defaults for older payloads or generic session reports.
  const procedureLabel = report.procedure_label || "Procedure";
  const postLabel = report.post_label || "Follow-Up Tasks";
  return [
    { id: "executive-summary", number: 1, title: "Executive Summary", visible: !!report.executive_summary },
    { id: "scope", number: 2, title: "Scope", visible: !!(report.scope || report.out_of_scope) },
    { id: "environment", number: 3, title: "Target Environment", visible: report.environment.length > 0 },
    { id: "prerequisites", number: 4, title: "Prerequisites", visible: report.prerequisites.length > 0 },
    { id: "procedure", number: 5, title: procedureLabel, visible: report.procedure.length > 0 },
    { id: "validation", number: 6, title: "Validation Checks", visible: report.validation.length > 0 },
    { id: "post-install", number: 7, title: postLabel, visible: report.post_install_tasks.length > 0 },
    { id: "troubleshooting", number: 8, title: "Troubleshooting", visible: report.troubleshooting.length > 0 },
    { id: "references", number: 9, title: "References", visible: report.references.length > 0 },
    { id: "metrics", number: 10, title: "Session Metrics", visible: !!report.session_metrics },
    { id: "warnings", number: 11, title: "Items Requiring Manual Review", visible: report.warnings.length > 0 },
    { id: "sign-off", number: 12, title: "Sign-Off", visible: true },
  ];
}

// HTML escaper for the standalone-document renderer. Just enough to keep
// content from breaking the markup; the document only contains user-typed
// values + LLM output, neither of which should contain raw HTML.
function esc(s: string | undefined | null): string {
  if (s === undefined || s === null) return "";
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

// Build a fully self-contained HTML document for download / print. We do
// NOT serialize the live MUI DOM — its emotion class names wouldn't apply
// in a new window and the layout would collapse. Instead we re-emit the
// report as plain semantic HTML with inlined print-friendly CSS so PDFs
// flow across pages cleanly.
function renderReportHtml(report: StructuredReport): string {
  const env = (report.environment || []).map((g) => {
    const rows = g.items
      .map(
        (it) =>
          `<tr><th>${esc(it.label)}</th><td>${esc(it.value)}</td></tr>`
      )
      .join("");
    return `<h3>${esc(g.category)}</h3><table>${rows}</table>`;
  }).join("\n");

  const prereqs = (report.prerequisites || [])
    .map(
      (p) =>
        `<li><strong>${esc(p.item)}</strong>${
          p.rationale ? ` — ${esc(p.rationale)}` : ""
        }</li>`
    )
    .join("");

  const procedure = (report.procedure || [])
    .map((s) => {
      const meta: string[] = [];
      if (s.duration_human) meta.push(`Duration: ${esc(s.duration_human)}`);
      if (s.completed_by) meta.push(`Performed by: ${esc(s.completed_by)}`);
      if ((s.confidence || "HIGH").toUpperCase() === "LOW") {
        meta.push(`Confidence: LOW (review source)`);
      }
      const refLinks = (s.references || [])
        .map((u) =>
          u.startsWith("http")
            ? `<a href="${esc(u)}">${esc(u)}</a>`
            : esc(u)
        )
        .join(", ");
      if (refLinks) meta.push(`Reference: ${refLinks}`);
      return `
<section class="step">
  <h3>Step ${s.number}. ${esc(s.title)}</h3>
  ${s.description ? `<p>${esc(s.description)}</p>` : ""}
  ${s.command ? `<pre>${esc(s.command)}</pre>` : ""}
  ${s.expected_outcome ? `<p><strong>Expected outcome.</strong> ${esc(s.expected_outcome)}</p>` : ""}
  ${
    s.evidence
      ? `<p><strong>Evidence captured during install:</strong></p><pre class="evidence">${esc(s.evidence)}</pre>`
      : ""
  }
  ${meta.length ? `<p class="step-meta">${meta.join(" · ")}</p>` : ""}
</section>`;
    })
    .join("");

  const validation = (report.validation || [])
    .map(
      (v) =>
        `<li><strong>${esc(v.check)}</strong> — ${esc(v.how_to_verify)}${
          v.pass_criteria ? ` <em>(pass criteria: ${esc(v.pass_criteria)})</em>` : ""
        }</li>`
    )
    .join("");

  const post = (report.post_install_tasks || [])
    .map(
      (t) =>
        `<li><span class="prio">${esc((t.priority || "MEDIUM").toUpperCase())}</span> ${esc(t.task)}</li>`
    )
    .join("");

  const trouble = (report.troubleshooting || [])
    .map(
      (t) => `
<div class="trouble">
  <p><strong>Symptom.</strong> ${esc(t.symptom)}</p>
  <p class="muted">Likely cause: ${esc(t.likely_cause)}</p>
  <p class="muted">Remediation: ${esc(t.remediation)}</p>
</div>`
    )
    .join("");

  const refs = (report.references || [])
    .map((r) => {
      const isUrl = r.is_url ?? r.url.startsWith("http");
      const failed = !r.fetched;
      const label = isUrl
        ? `<a href="${esc(r.url)}">${esc(r.title)}</a>`
        : `<span class="corpus-ref">${esc(r.title)}</span>`;
      const tag = isUrl
        ? failed
          ? ` <em class="warn">(failed to fetch — review manually)</em>`
          : ""
        : ` <span class="tag">local corpus</span>`;
      return `<li>${label}${tag}</li>`;
    })
    .join("");

  const warnings = (report.warnings || [])
    .map(
      (w) =>
        `<li><strong>[${esc(w.kind)}]</strong> ${
          w.step_number ? `Step ${w.step_number}: ` : ""
        }${esc(w.message)}</li>`
    )
    .join("");

  const sm = report.session_metrics || ({} as StructuredReport["session_metrics"]);
  const headerRows: Array<[string, string]> = [
    ...(report.is_customer_template
      ? ([["Customer", report.customer_placeholder || "—"]] as Array<[string, string]>)
      : []),
    ["Document version", report.doc_version],
    ["Generated", formatDate(report.generated_at) || report.generated_at],
    ["Generated by", report.generated_by],
    ["Source corpus", formatDate(report.corpus_version) || report.corpus_version || "n/a"],
    ["Session ID", report.session_id],
  ];
  const headerHtml = headerRows
    .map(([k, v]) => `<tr><th>${esc(k)}</th><td>${esc(v)}</td></tr>`)
    .join("");

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>${esc(report.title)}</title>
<style>
  @page { size: A4; margin: 16mm 14mm 18mm 14mm; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body {
    font: 11pt/1.55 Georgia, "Times New Roman", serif;
    color: #1a1a1a;
    max-width: 780px;
    margin: 0 auto;
    padding: 24px 28px;
    background: #fff;
  }
  h1, h2, h3, h4, .sans { font-family: -apple-system, "Segoe UI", system-ui, sans-serif; letter-spacing: -0.01em; }
  h1 { font-size: 22pt; margin: 0 0 4pt 0; }
  h2 { font-size: 14pt; margin: 22pt 0 6pt 0; border-bottom: 1px solid #ddd; padding-bottom: 4pt; page-break-after: avoid; }
  h3 { font-size: 11.5pt; margin: 14pt 0 4pt 0; page-break-after: avoid; }
  p { margin: 4pt 0 8pt 0; }
  table { border-collapse: collapse; width: 100%; margin: 6pt 0 10pt 0; page-break-inside: avoid; }
  td, th { border: 1px solid #ccc; padding: 5pt 8pt; text-align: left; vertical-align: top; font-family: -apple-system, "Segoe UI", system-ui, sans-serif; font-size: 10pt; }
  th { background: #f4f4f4; font-weight: 600; width: 32%; }
  ul { margin: 4pt 0 8pt 0; padding-left: 20pt; }
  li { margin: 2pt 0; }
  pre { background: #f6f6f6; border: 1px solid #e1e1e1; border-radius: 4px; padding: 8pt; overflow-x: auto; white-space: pre-wrap; word-break: break-word; font-family: "SF Mono", Menlo, Consolas, monospace; font-size: 9.5pt; page-break-inside: avoid; }
  pre.evidence { max-height: none; }
  .doc-subtitle { color: #555; font-style: italic; margin: 0 0 14pt 0; }
  .doc-note { color: #777; font-size: 9pt; margin-top: 4pt; }
  .section-num { color: #999; margin-right: 6pt; font-variant-numeric: tabular-nums; }
  .step { page-break-inside: avoid; margin-top: 12pt; }
  .step-meta { color: #555; font-size: 9.5pt; font-family: -apple-system, "Segoe UI", system-ui, sans-serif; margin-top: 4pt; }
  .muted { color: #555; font-size: 10pt; }
  .warn { color: #8a4b00; }
  .corpus-ref { font-family: "SF Mono", Menlo, Consolas, monospace; font-size: 10pt; color: #444; }
  .tag { display: inline-block; padding: 0 6pt; border: 1px solid #bbb; border-radius: 3pt; font-family: -apple-system, "Segoe UI", system-ui, sans-serif; font-size: 8.5pt; color: #555; margin-left: 4pt; }
  .prio { display: inline-block; min-width: 56pt; padding: 0 6pt; border: 1px solid #bbb; border-radius: 3pt; font-family: -apple-system, "Segoe UI", system-ui, sans-serif; font-size: 8.5pt; color: #444; margin-right: 6pt; text-align: center; }
  .trouble { border-left: 3px solid #ccc; padding: 0 0 0 10pt; margin: 8pt 0; page-break-inside: avoid; }
  .footer { margin-top: 24pt; padding-top: 8pt; border-top: 1px solid #ddd; color: #888; font-size: 9pt; font-family: -apple-system, "Segoe UI", system-ui, sans-serif; }
  a { color: #0b57d0; text-decoration: underline; }
  @media print { body { padding: 0; max-width: none; } a { color: #1a1a1a; } }
</style>
</head>
<body>
  <h1>${esc(report.title)}</h1>
  ${report.subtitle ? `<p class="doc-subtitle">${esc(report.subtitle)}</p>` : ""}
  <table>${headerHtml}</table>
  ${
    report.is_customer_template
      ? `<p class="doc-note">This document is a per-customer installation template. Replace the customer placeholder above and complete the sign-off table before distribution.</p>`
      : ""
  }

  ${
    report.executive_summary
      ? `<h2><span class="section-num">1.</span>Executive Summary</h2><p>${esc(report.executive_summary)}</p>`
      : ""
  }

  ${
    report.scope || report.out_of_scope
      ? `<h2><span class="section-num">2.</span>Scope</h2>
         ${report.scope ? `<p><strong>In scope.</strong> ${esc(report.scope)}</p>` : ""}
         ${report.out_of_scope ? `<p><strong>Out of scope.</strong> ${esc(report.out_of_scope)}</p>` : ""}
         ${report.audience ? `<p class="muted"><strong>Audience.</strong> ${esc(report.audience)}</p>` : ""}`
      : ""
  }

  ${
    env
      ? `<h2><span class="section-num">3.</span>Target Environment</h2>${env}`
      : ""
  }

  ${
    prereqs
      ? `<h2><span class="section-num">4.</span>Prerequisites</h2><ul>${prereqs}</ul>`
      : ""
  }

  ${
    procedure
      ? `<h2><span class="section-num">5.</span>${report.is_customer_template ? "Installation Procedure" : "Procedure"}</h2>${procedure}`
      : ""
  }

  ${
    validation
      ? `<h2><span class="section-num">6.</span>Validation Checks</h2><ul>${validation}</ul>`
      : ""
  }

  ${
    post
      ? `<h2><span class="section-num">7.</span>${report.is_customer_template ? "Post-Installation Tasks" : "Follow-Up Tasks"}</h2><ul>${post}</ul>`
      : ""
  }

  ${
    trouble
      ? `<h2><span class="section-num">8.</span>Troubleshooting</h2>${trouble}`
      : ""
  }

  ${
    refs
      ? `<h2><span class="section-num">9.</span>References</h2><ul>${refs}</ul>`
      : ""
  }

  <h2><span class="section-num">10.</span>Session Metrics</h2>
  <table>
    <tr><th>Started</th><td>${esc(formatDate(sm.started_at) || "—")}</td></tr>
    <tr><th>Completed</th><td>${esc(formatDate(sm.completed_at) || "—")}</td></tr>
    <tr><th>Total duration</th><td>${esc(sm.duration_human || "—")}</td></tr>
    <tr><th>Steps</th><td>${esc(`${sm.completed_steps || 0} / ${sm.step_count || 0} completed`)}</td></tr>
    <tr><th>Low-confidence steps</th><td>${esc(String(sm.low_confidence_steps || 0))}</td></tr>
  </table>

  ${
    warnings
      ? `<h2><span class="section-num">11.</span>Items Requiring Manual Review</h2><ul>${warnings}</ul>`
      : ""
  }

  <h2><span class="section-num">12.</span>Sign-Off</h2>
  <table>
    <thead><tr><th>Role</th><th>Name</th><th>Signature</th><th>Date</th></tr></thead>
    <tbody>
      <tr><td>Prepared by</td><td>${esc(report.sign_off.prepared_by || "_______________")}</td><td>_______________</td><td>_______________</td></tr>
      <tr><td>Reviewed by</td><td>${esc(report.sign_off.reviewed_by || "_______________")}</td><td>_______________</td><td>_______________</td></tr>
      <tr><td>Approved by</td><td>${esc(report.sign_off.approved_by || "_______________")}</td><td>_______________</td><td>_______________</td></tr>
    </tbody>
  </table>

  <p class="footer">Generated by Q Interrogator · session ${esc(report.session_id)} · schema v${esc(String(report.schema_version))}</p>
</body>
</html>`;
}

export default function ReportPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const auth = useAuth();
  const { sessionId } = useParams<{ sessionId: string }>();

  const [report, setReport] = useState<StructuredReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeSectionId, setActiveSectionId] = useState<string>("executive-summary");
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getSessionReport(sessionId, auth)
      .then((r) => {
        if (cancelled) return;
        setReport(r);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : "Failed to load report");
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId, auth]);

  const sections = useMemo(() => (report ? buildSections(report) : []), [report]);

  // Scroll-spy: highlight the TOC entry that matches the section currently
  // closest to the top of the viewport. IntersectionObserver instead of a
  // scroll listener so it doesn't fire on every pixel.
  useEffect(() => {
    if (!report) return;
    const visibleSections = sections.filter((s) => s.visible);
    const observers: IntersectionObserver[] = [];
    visibleSections.forEach((section) => {
      const el = document.getElementById(section.id);
      if (!el) return;
      const obs = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setActiveSectionId(section.id);
            }
          });
        },
        { rootMargin: "-30% 0px -60% 0px", threshold: 0 }
      );
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, [report, sections]);

  const handleCopyMarkdown = async () => {
    if (!report) return;
    try {
      await navigator.clipboard.writeText(report.raw_markdown);
      setToast("Copied as Markdown");
    } catch {
      setToast("Copy failed — clipboard unavailable");
    }
  };

  const handleDownload = (kind: "md" | "html" | "ttl") => {
    if (!report) return;
    const fileBase = (report.template_id || (report.report_type === 'interrogation' ? 'interrogation-report' : 'installation-report')).replace(/[^a-z0-9_-]/gi, "_");
    const ts = new Date().toISOString().slice(0, 10);
    let mime = "text/plain";
    let body = "";
    let ext = "txt";
    if (kind === "md") {
      mime = "text/markdown;charset=utf-8";
      body = report.raw_markdown;
      ext = "md";
    } else if (kind === "html") {
      mime = "text/html;charset=utf-8";
      body = renderReportHtml(report);
      ext = "html";
    } else if (kind === "ttl") {
      mime = "text/turtle;charset=utf-8";
      body = report.interrogation_turtle || "";
      ext = "ttl";
    }
    const blob = new Blob([body], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${fileBase}_${ts}.${ext}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setToast(`Downloaded ${ext.toUpperCase()}`);
  };

  // Open the standalone print-ready HTML in a new window and trigger print
  // there. We deliberately do NOT call window.print() on the live page —
  // the app shell uses 100dvh + overflow:hidden chains that browsers can't
  // unwind via @media print, which is what produced the previous "1 page
  // with a scrollbar" PDF. The standalone document has no app chrome, so
  // pagination is clean and the PDF spans as many pages as needed.
  const handlePrint = () => {
    if (!report) return;
    const html = renderReportHtml(report);
    const w = window.open("", "_blank");
    if (!w) {
      setToast("Allow pop-ups to print this report");
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
    // Give the new window a tick to lay out before invoking print so the
    // first PDF page isn't blank.
    const trigger = () => {
      try {
        w.focus();
        w.print();
      } catch {
        /* user can still print via the new window's menu */
      }
    };
    if (w.document.readyState === "complete") {
      setTimeout(trigger, 200);
    } else {
      w.addEventListener("load", () => setTimeout(trigger, 200));
    }
  };

  const handleScrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    setActiveSectionId(id);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1, gap: 2, p: 4 }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Compiling installation report…
        </Typography>
      </Box>
    );
  }

  if (error || !report) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1, gap: 2, p: 4 }}>
        <Typography variant="h6">Could not load report</Typography>
        <Typography variant="body2" color="text.secondary">
          {error || "The report is unavailable."}
        </Typography>
        <Button variant="outlined" onClick={() => navigate(-1)}>
          Go back
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        bgcolor: alpha(theme.palette.text.primary, 0.04),
        // Print CSS — when the user prints, hide everything except the doc
        // body, drop the page background, and restore black-on-white text.
        "@media print": {
          bgcolor: "white",
          "& .no-print": { display: "none !important" },
          "& #doc-printable": { boxShadow: "none !important", border: "none !important", maxWidth: "none !important", margin: 0, padding: 0 },
        },
      }}
    >
      {/* Toolbar */}
      <Box
        className="no-print"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: { xs: 2, md: 3 },
          py: 1.25,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          bgcolor: theme.palette.background.paper,
          flexShrink: 0,
        }}
      >
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} size="small" sx={{ textTransform: "none", fontWeight: 600 }}>
          Back to chat
        </Button>
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Copy Markdown to clipboard">
          <IconButton size="small" onClick={handleCopyMarkdown}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Download as Markdown (.md)">
          <Button
            size="small"
            variant="outlined"
            startIcon={<CodeIcon />}
            onClick={() => handleDownload("md")}
            sx={{ textTransform: "none", fontWeight: 500 }}
          >
            .md
          </Button>
        </Tooltip>
        {report.report_type === 'interrogation' && report.interrogation_turtle?.trim() && (
          <Tooltip title="Download knowledge graph as Turtle (.ttl)">
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => handleDownload("ttl")}
              sx={{ textTransform: "none", fontWeight: 500 }}
            >
              .ttl
            </Button>
          </Tooltip>
        )}
        <Tooltip title="Download as standalone HTML (printable)">
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => handleDownload("html")}
            sx={{ textTransform: "none", fontWeight: 500 }}
          >
            .html
          </Button>
        </Tooltip>
        <Tooltip title="Print or save as PDF">
          <Button
            size="small"
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Print / PDF
          </Button>
        </Tooltip>
      </Box>

      {/* Body: TOC sidebar + scrollable document */}
      <Box sx={{ flex: 1, display: "flex", overflow: "hidden", minHeight: 0 }}>
        {/* TOC */}
        <Box
          className="no-print"
          sx={{
            width: TOC_WIDTH,
            flexShrink: 0,
            borderRight: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
            bgcolor: theme.palette.background.paper,
            overflowY: "auto",
            display: { xs: "none", md: "block" },
            py: 2.5,
            px: 2,
          }}
        >
          <Typography variant="overline" sx={{ color: "text.secondary", fontWeight: 700, letterSpacing: "0.08em" }}>
            Contents
          </Typography>
          <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 0.25 }}>
            {sections
              .filter((s) => s.visible)
              .map((s) => {
                const active = s.id === activeSectionId;
                return (
                  <Box
                    key={s.id}
                    onClick={() => handleScrollToSection(s.id)}
                    sx={{
                      cursor: "pointer",
                      px: 1.25,
                      py: 0.75,
                      borderRadius: 1,
                      borderLeft: `3px solid ${active ? theme.palette.primary.main : "transparent"}`,
                      bgcolor: active ? alpha(theme.palette.primary.main, 0.08) : "transparent",
                      "&:hover": {
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                      },
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: active ? "primary.main" : "text.primary",
                        fontWeight: active ? 600 : 400,
                      }}
                    >
                      <Box component="span" sx={{ color: "text.disabled", mr: 1, fontVariantNumeric: "tabular-nums" }}>
                        {s.number}.
                      </Box>
                      {s.title}
                    </Typography>
                  </Box>
                );
              })}
          </Box>
        </Box>

        {/* Document body — Paper grows to content height. The previous
            version put `<Paper>` inside a flex container without
            alignItems, so default `stretch` made the Paper exactly viewport
            height while content overflowed past its bottom border. We now
            align to flex-start so the Paper's box always wraps its content. */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            py: { xs: 2, md: 4 },
            px: { xs: 1, md: 3 },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              maxWidth: DOC_MAX_WIDTH,
              bgcolor: "background.paper",
              border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              borderRadius: 1,
              boxShadow: "0 1px 4px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.06)",
              p: { xs: 3, md: 6 },
              fontFamily: 'Georgia, "Times New Roman", serif',
              color: "text.primary",
              lineHeight: 1.65,
              "& h1, & h2, & h3, & h4": {
                fontFamily: '-apple-system, "Segoe UI", system-ui, sans-serif',
                color: "text.primary",
                letterSpacing: "-0.01em",
              },
            }}
          >
            <DocumentBody report={report} />
          </Paper>
        </Box>
      </Box>

      <Snackbar
        open={!!toast}
        autoHideDuration={2200}
        onClose={() => setToast(null)}
        message={toast || ""}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Document body — pure presentational, no fetching. Split out so the print
// HTML export can re-use the rendered DOM via #doc-printable.
// ---------------------------------------------------------------------------

function SectionHeading({ id, number, title }: { id: string; number: number; title: string }) {
  return (
    <Typography
      id={id}
      component="h2"
      sx={{
        fontSize: "1.25rem",
        fontWeight: 700,
        mt: 5,
        mb: 1.5,
        pb: 0.75,
        borderBottom: 1,
        borderColor: "divider",
        scrollMarginTop: 16,
      }}
    >
      <Box component="span" sx={{ color: "text.disabled", mr: 1, fontVariantNumeric: "tabular-nums" }}>
        {number}.
      </Box>
      {title}
    </Typography>
  );
}

function MetaTable({ rows }: { rows: Array<[string, string]> }) {
  return (
    <Box
      component="table"
      sx={{
        borderCollapse: "collapse",
        width: "100%",
        my: 1.5,
        "& td, & th": {
          border: 1,
          borderColor: "divider",
          padding: "8px 12px",
          textAlign: "left",
          verticalAlign: "top",
          fontSize: "0.95rem",
          fontFamily: '-apple-system, "Segoe UI", system-ui, sans-serif',
        },
        "& th": { bgcolor: (t) => alpha(t.palette.text.primary, 0.04), fontWeight: 600, width: "32%" },
      }}
    >
      <tbody>
        {rows.map(([k, v]) => (
          <tr key={k}>
            <th>{k}</th>
            <td>{v || "—"}</td>
          </tr>
        ))}
      </tbody>
    </Box>
  );
}

function EnvironmentTable({ group }: { group: ReportEnvironmentGroup }) {
  return (
    <Box sx={{ mt: 2 }}>
      <Typography component="h3" sx={{ fontSize: "1.05rem", fontWeight: 600, mb: 1 }}>
        {group.category}
      </Typography>
      <MetaTable rows={group.items.map((it) => [it.label, it.value])} />
    </Box>
  );
}

function ProcedureStepBlock({ step }: { step: ReportProcedureStep }) {
  const theme = useTheme();
  const isLow = (step.confidence || "HIGH").toUpperCase() === "LOW";
  return (
    <Box sx={{ mt: 3, pageBreakInside: "avoid" }}>
      <Typography component="h3" sx={{ fontSize: "1.05rem", fontWeight: 600, mb: 0.5 }}>
        Step {step.number}. {step.title}
      </Typography>
      {step.description && (
        <Typography sx={{ mb: 1.5 }}>{step.description}</Typography>
      )}
      {step.command && (
        <Box
          component="pre"
          sx={{
            bgcolor: alpha(theme.palette.text.primary, 0.04),
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            p: 1.5,
            overflowX: "auto",
            fontFamily: '"SF Mono", Menlo, Consolas, monospace',
            fontSize: "0.875rem",
            my: 1.5,
          }}
        >
          {step.command}
        </Box>
      )}
      {step.expected_outcome && (
        <Typography sx={{ my: 1 }}>
          <Box component="strong">Expected outcome.</Box> {step.expected_outcome}
        </Typography>
      )}
      {step.evidence && (
        <Box sx={{ my: 1.5 }}>
          <Typography sx={{ mb: 0.5, fontWeight: 600 }}>Evidence captured during install:</Typography>
          <Box
            component="pre"
            sx={{
              bgcolor: alpha(theme.palette.text.primary, 0.03),
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              p: 1.25,
              overflowX: "auto",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              fontFamily: '"SF Mono", Menlo, Consolas, monospace',
              fontSize: "0.85rem",
              maxHeight: 320,
              overflowY: "auto",
            }}
          >
            {step.evidence}
          </Box>
        </Box>
      )}
      <Box
        sx={{
          mt: 1,
          display: "flex",
          gap: 1.5,
          flexWrap: "wrap",
          color: "text.secondary",
          fontSize: "0.85rem",
          fontFamily: '-apple-system, "Segoe UI", system-ui, sans-serif',
        }}
      >
        {step.duration_human && <span>Duration: {step.duration_human}</span>}
        {step.completed_by && <span>Performed by: {step.completed_by}</span>}
        {isLow && (
          <Chip
            size="small"
            color="warning"
            variant="outlined"
            label="Low confidence — review source"
            icon={<WarningAmberIcon />}
            sx={{ height: 22 }}
          />
        )}
        {step.references.map((url) => (
          <Box
            key={url}
            component="a"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ color: "primary.main", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 0.4 }}
          >
            <OpenInNewIcon sx={{ fontSize: 14 }} /> Source
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function DocumentBody({ report }: { report: StructuredReport }) {
  if (report.report_type === 'interrogation') {
    return (
      <Box>
        <Box>
          <Typography component="h1" sx={{ fontSize: { xs: "1.75rem", md: "2rem" }, fontWeight: 700, mb: 0.5 }}>
            {report.title}
          </Typography>
          {report.subtitle && (
            <Typography sx={{ color: "text.secondary", fontStyle: "italic", mb: 2.5 }}>
              {report.subtitle}
            </Typography>
          )}
          <MetaTable
            rows={[
              ["Document version", report.doc_version],
              ["Generated", formatDate(report.generated_at) || report.generated_at],
              ["Generated by", report.generated_by],
              ["Session ID", report.session_id],
              ["Turns processed", String(report.interrogation_turn_count ?? report.session_metrics.completed_steps ?? 0)],
              ["Transcript segments", String(report.interrogation_segment_count ?? 0)],
            ]}
          />
          <Divider sx={{ my: 4 }} />
        </Box>

        {report.executive_summary && (
          <Box>
            <SectionHeading id="executive-summary" number={1} title="Summary" />
            <Typography>{report.executive_summary}</Typography>
          </Box>
        )}

        {report.environment.length > 0 && (
          <Box>
            <SectionHeading id="environment" number={2} title="Entities" />
            {report.environment.map((g) => (
              <EnvironmentTable key={g.category} group={g} />
            ))}
          </Box>
        )}

        {report.post_install_tasks.length > 0 && (
          <Box>
            <SectionHeading id="post-install" number={3} title={report.post_label || "Open Threads"} />
            <Box component="ul" sx={{ pl: 3, my: 1, "& li": { mb: 0.75 } }}>
              {report.post_install_tasks.map((t, i) => (
                <li key={i}>{t.task}</li>
              ))}
            </Box>
          </Box>
        )}

        {report.references.length > 0 && (
          <Box>
            <SectionHeading id="references" number={4} title="Claim References" />
            <Box component="ul" sx={{ pl: 3, my: 1 }}>
              {report.references.map((r) => (
                <li key={r.url + r.title}>{r.title}</li>
              ))}
            </Box>
          </Box>
        )}

        {report.interrogation_turtle?.trim() && (
          <Box>
            <SectionHeading id="knowledge-graph" number={5} title="Knowledge Graph" />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              RDF triples extracted during the interview. Download the `.ttl` file from the toolbar for import into a graph tool.
            </Typography>
            <Box
              component="pre"
              sx={{
                bgcolor: (t) => alpha(t.palette.text.primary, 0.04),
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                p: 1.5,
                overflowX: "auto",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontFamily: '"SF Mono", Menlo, Consolas, monospace',
                fontSize: "0.85rem",
                maxHeight: 420,
                overflowY: "auto",
              }}
            >
              {report.interrogation_turtle}
            </Box>
          </Box>
        )}

        <Box>
          <SectionHeading id="metrics" number={6} title="Session Metrics" />
          <MetaTable
            rows={[
              ["Started", formatDate(report.session_metrics.started_at) || "—"],
              ["Completed", formatDate(report.session_metrics.completed_at) || "—"],
              ["Total duration", report.session_metrics.duration_human || "—"],
              ["Turns processed", String(report.interrogation_turn_count ?? report.session_metrics.completed_steps ?? 0)],
              ["Transcript segments", String(report.interrogation_segment_count ?? 0)],
            ]}
          />
        </Box>

        <Box>
          <SectionHeading id="sign-off" number={7} title="Sign-Off" />
          <MetaTable rows={[["Prepared by", report.sign_off.prepared_by || "—"]]} />
        </Box>

        <Box sx={{ mt: 6, pt: 2, borderTop: 1, borderColor: "divider", color: "text.disabled", fontSize: "0.8rem" }}>
          Generated by Q Interrogator · session {report.session_id} · schema v
          {report.schema_version}
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {/* Cover */}
      <Box>
        <Typography component="h1" sx={{ fontSize: { xs: "1.75rem", md: "2rem" }, fontWeight: 700, mb: 0.5 }}>
          {report.title}
        </Typography>
        {report.subtitle && (
          <Typography sx={{ color: "text.secondary", fontStyle: "italic", mb: 2.5 }}>
            {report.subtitle}
          </Typography>
        )}
        <MetaTable
          rows={[
            // Customer row only for per-customer install templates. For
            // generic session reports the row is irrelevant and confusing.
            ...(report.is_customer_template
              ? ([["Customer", report.customer_placeholder || "—"]] as Array<[string, string]>)
              : []),
            ["Document version", report.doc_version],
            ["Generated", formatDate(report.generated_at) || report.generated_at],
            ["Generated by", report.generated_by],
            ["Source corpus", formatDate(report.corpus_version) || report.corpus_version || "n/a"],
            ["Session ID", report.session_id],
          ]}
        />
        {report.is_customer_template && (
          <Typography variant="caption" sx={{ display: "block", color: "text.secondary", mt: 1 }}>
            This document is a per-customer installation template. Replace the customer
            placeholder above and complete the sign-off table before distribution.
          </Typography>
        )}
        <Divider sx={{ my: 4 }} />
      </Box>

      {/* 1. Executive Summary */}
      {report.executive_summary && (
        <Box>
          <SectionHeading id="executive-summary" number={1} title="Executive Summary" />
          <Typography>{report.executive_summary}</Typography>
        </Box>
      )}

      {/* 2. Scope */}
      {(report.scope || report.out_of_scope) && (
        <Box>
          <SectionHeading id="scope" number={2} title="Scope" />
          {report.scope && (
            <Typography sx={{ mb: 1.5 }}>
              <Box component="strong">In scope.</Box> {report.scope}
            </Typography>
          )}
          {report.out_of_scope && (
            <Typography sx={{ mb: 1.5 }}>
              <Box component="strong">Out of scope.</Box> {report.out_of_scope}
            </Typography>
          )}
          {report.audience && (
            <Typography sx={{ color: "text.secondary" }}>
              <Box component="strong">Audience.</Box> {report.audience}
            </Typography>
          )}
        </Box>
      )}

      {/* 3. Environment */}
      {report.environment.length > 0 && (
        <Box>
          <SectionHeading id="environment" number={3} title="Target Environment" />
          {report.environment.map((g) => (
            <EnvironmentTable key={g.category} group={g} />
          ))}
        </Box>
      )}

      {/* 4. Prerequisites */}
      {report.prerequisites.length > 0 && (
        <Box>
          <SectionHeading id="prerequisites" number={4} title="Prerequisites" />
          <Box component="ul" sx={{ pl: 3, my: 1, "& li": { mb: 0.75 } }}>
            {report.prerequisites.map((p, i) => (
              <li key={i}>
                <Box component="strong">{p.item}</Box>
                {p.rationale ? ` — ${p.rationale}` : ""}
              </li>
            ))}
          </Box>
        </Box>
      )}

      {/* 5. Procedure. The heading text comes from the report payload so
          each template (install, tuning, ...) can supply its own wording. */}
      {report.procedure.length > 0 && (
        <Box>
          <SectionHeading
            id="procedure"
            number={5}
            title={report.procedure_label || "Procedure"}
          />
          {report.procedure.map((step) => (
            <ProcedureStepBlock key={step.number} step={step} />
          ))}
        </Box>
      )}

      {/* 6. Validation */}
      {report.validation.length > 0 && (
        <Box>
          <SectionHeading id="validation" number={6} title="Validation Checks" />
          <Box component="ul" sx={{ pl: 3, my: 1, "& li": { mb: 1 } }}>
            {report.validation.map((v, i) => (
              <li key={i}>
                <Box component="strong">{v.check}</Box> — {v.how_to_verify}
                {v.pass_criteria && (
                  <Box component="span" sx={{ color: "text.secondary", fontStyle: "italic" }}>
                    {" "}
                    (pass criteria: {v.pass_criteria})
                  </Box>
                )}
              </li>
            ))}
          </Box>
        </Box>
      )}

      {/* 7. Post-task list. Heading wording comes from the report payload
          (e.g. "Post-Installation Tasks", "Post-Tuning Tasks", "Follow-Up
          Tasks"). */}
      {report.post_install_tasks.length > 0 && (
        <Box>
          <SectionHeading
            id="post-install"
            number={7}
            title={report.post_label || "Follow-Up Tasks"}
          />
          <Box component="ul" sx={{ pl: 3, my: 1, "& li": { mb: 0.75 } }}>
            {report.post_install_tasks.map((t, i) => (
              <li key={i}>
                <PriorityBadge priority={t.priority} /> {t.task}
              </li>
            ))}
          </Box>
        </Box>
      )}

      {/* 8. Troubleshooting */}
      {report.troubleshooting.length > 0 && (
        <Box>
          <SectionHeading id="troubleshooting" number={8} title="Troubleshooting" />
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {report.troubleshooting.map((t, i) => (
              <Box key={i} sx={{ borderLeft: 3, borderColor: "divider", pl: 1.5 }}>
                <Typography sx={{ mb: 0.25 }}>
                  <Box component="strong">Symptom.</Box> {t.symptom}
                </Typography>
                <Typography sx={{ color: "text.secondary", fontSize: "0.95em" }}>
                  Likely cause: {t.likely_cause}
                </Typography>
                <Typography sx={{ color: "text.secondary", fontSize: "0.95em" }}>
                  Remediation: {t.remediation}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* 9. References — http(s) URLs render as links; corpus:// paths and
          other internal identifiers render as plain text with a "local
          corpus" tag, since they don't resolve to anything navigable. */}
      {report.references.length > 0 && (
        <Box>
          <SectionHeading id="references" number={9} title="References" />
          <Box component="ul" sx={{ pl: 3, my: 1 }}>
            {report.references.map((r) => {
              const isUrl = r.is_url ?? r.url.startsWith("http");
              return (
                <li key={r.url}>
                  {isUrl ? (
                    <Box
                      component="a"
                      href={r.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ color: "primary.main", textDecoration: "underline" }}
                    >
                      {r.title}
                    </Box>
                  ) : (
                    <Box
                      component="span"
                      sx={{
                        fontFamily: '"SF Mono", Menlo, Consolas, monospace',
                        fontSize: "0.95em",
                        color: "text.primary",
                      }}
                    >
                      {r.title}
                    </Box>
                  )}
                  {isUrl && !r.fetched && (
                    <Box component="span" sx={{ color: "warning.dark", ml: 1, fontStyle: "italic" }}>
                      (failed to fetch — review manually)
                    </Box>
                  )}
                  {!isUrl && (
                    <Box
                      component="span"
                      sx={{
                        ml: 1,
                        px: 0.75,
                        py: 0.1,
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 0.5,
                        color: "text.secondary",
                        fontSize: "0.75em",
                        fontFamily: '-apple-system, "Segoe UI", system-ui, sans-serif',
                      }}
                    >
                      local corpus
                    </Box>
                  )}
                </li>
              );
            })}
          </Box>
        </Box>
      )}

      {/* 10. Metrics */}
      <Box>
        <SectionHeading id="metrics" number={10} title="Session Metrics" />
        <MetaTable
          rows={[
            ["Started", formatDate(report.session_metrics.started_at) || "—"],
            ["Completed", formatDate(report.session_metrics.completed_at) || "—"],
            ["Total duration", report.session_metrics.duration_human || "—"],
            [
              "Steps",
              `${report.session_metrics.completed_steps} / ${report.session_metrics.step_count} completed`,
            ],
            ["Low-confidence steps", String(report.session_metrics.low_confidence_steps)],
          ]}
        />
      </Box>

      {/* 11. Warnings */}
      {report.warnings.length > 0 && (
        <Box>
          <SectionHeading id="warnings" number={11} title="Items Requiring Manual Review" />
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {report.warnings.map((w, i) => (
              <Box
                key={i}
                sx={{
                  display: "flex",
                  gap: 1,
                  alignItems: "flex-start",
                  p: 1.25,
                  borderRadius: 1,
                  bgcolor: (t) => alpha(t.palette.warning.main, 0.08),
                  border: 1,
                  borderColor: (t) => alpha(t.palette.warning.main, 0.3),
                }}
              >
                <WarningAmberIcon sx={{ fontSize: 18, color: "warning.dark", mt: 0.25 }} />
                <Typography sx={{ fontSize: "0.95rem" }}>
                  <Box component="strong" sx={{ mr: 0.5 }}>
                    [{w.kind}]
                  </Box>
                  {w.step_number ? `Step ${w.step_number}: ` : ""}
                  {w.message}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* 12. Sign-Off */}
      <Box>
        <SectionHeading id="sign-off" number={12} title="Sign-Off" />
        <Box
          component="table"
          sx={{
            borderCollapse: "collapse",
            width: "100%",
            mt: 1.5,
            "& td, & th": {
              border: 1,
              borderColor: "divider",
              padding: "10px 12px",
              textAlign: "left",
              fontFamily: '-apple-system, "Segoe UI", system-ui, sans-serif',
              fontSize: "0.95rem",
            },
            "& th": { bgcolor: (t) => alpha(t.palette.text.primary, 0.04), fontWeight: 600 },
          }}
        >
          <thead>
            <tr>
              <th>Role</th>
              <th>Name</th>
              <th>Signature</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Prepared by</td>
              <td>{report.sign_off.prepared_by || "_______________"}</td>
              <td>_______________</td>
              <td>_______________</td>
            </tr>
            <tr>
              <td>Reviewed by</td>
              <td>{report.sign_off.reviewed_by || "_______________"}</td>
              <td>_______________</td>
              <td>_______________</td>
            </tr>
            <tr>
              <td>Approved by</td>
              <td>{report.sign_off.approved_by || "_______________"}</td>
              <td>_______________</td>
              <td>_______________</td>
            </tr>
          </tbody>
        </Box>
      </Box>

      {/* Footer */}
      <Box sx={{ mt: 6, pt: 2, borderTop: 1, borderColor: "divider", color: "text.disabled", fontSize: "0.8rem" }}>
        Generated by Q Interrogator · session {report.session_id} · schema v
        {report.schema_version}
      </Box>
    </Box>
  );
}

// Neutral, document-style priority label. The earlier coloured chip
// introduced reds/oranges that didn't appear anywhere else in the report —
// confusing readers who expected the colour to mean something they should
// check elsewhere. A plain bordered tag carries the priority information
// without implying a severity colour code.
function PriorityBadge({ priority }: { priority: string }) {
  const p = (priority || "MEDIUM").toUpperCase();
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        mr: 1,
        minWidth: 56,
        textAlign: "center",
        px: 0.75,
        py: 0.1,
        borderRadius: 0.5,
        border: 1,
        borderColor: "divider",
        fontSize: "0.7rem",
        fontWeight: 600,
        letterSpacing: "0.04em",
        color: "text.secondary",
        bgcolor: "transparent",
        fontFamily: '-apple-system, "Segoe UI", system-ui, sans-serif',
        verticalAlign: "middle",
      }}
    >
      {p}
    </Box>
  );
}
